/**
 * Spec extraction for the public image/video detail pages.
 *
 * Two sources, deliberately:
 *
 * 1. **Measured from the file** (resolution / aspect ratio / duration). The
 *    `images.request` JSON is written by ~30 different provider integrations
 *    and its shape is wildly inconsistent — a survey of 800 public rows found
 *    videos using `duration`/`aspectRatio`/`resolution` while images use
 *    `size`, `dimensions`, `aspect_ratio` or nothing at all (batch-regenerated
 *    rows carry none of it). Measuring the delivered file is the only thing
 *    that works for every row, and it reports what was actually produced
 *    rather than what was asked for.
 * 2. **Read from the row** for facts a file can't carry (model, quality,
 *    whether audio was generated, how the generation was seeded).
 */

/** Common ratios we snap to, so 1024x1024 reads "1:1" and 1280x718 reads "16:9". */
const COMMON_RATIOS: [number, number][] = [
  [1, 1],
  [4, 3],
  [3, 4],
  [3, 2],
  [2, 3],
  [16, 9],
  [9, 16],
  [5, 4],
  [4, 5],
  [21, 9],
  [9, 21],
  [2, 1],
  [1, 2],
  [7, 5],
  [5, 7],
];

const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);

/**
 * Encoders pad dimensions to multiples of 16, so real files land near a common
 * ratio rather than on it — a 9:16 clip is delivered as 496x864 (0.574, a 2%
 * miss) and an exact gcd would print "31:54". Snap to the CLOSEST common ratio
 * within a tolerance, falling back to a reduced fraction.
 *
 * The tolerance is bounded by the tightest gap between neighbouring ratios in
 * the list: 4:3 (1.333) and 7:5 (1.4) are only 5% apart, so anything under
 * half of that can't snap to the wrong neighbour.
 */
const RATIO_SNAP_TOLERANCE = 0.025;

export const formatAspectRatio = (
  width?: number,
  height?: number,
): string | null => {
  if (!width || !height) return null;
  const value = width / height;

  let closest: { label: string; distance: number } | null = null;
  for (const [w, h] of COMMON_RATIOS) {
    const distance = Math.abs(value - w / h) / (w / h);
    if (!closest || distance < closest.distance) {
      closest = { label: `${w}:${h}`, distance };
    }
  }
  if (closest && closest.distance <= RATIO_SNAP_TOLERANCE) return closest.label;

  const divisor = gcd(width, height);
  const w = Math.round(width / divisor);
  const h = Math.round(height / divisor);
  // A reduced fraction is only useful while it stays readable.
  if (w <= 40 && h <= 40) return `${w}:${h}`;
  return `${value.toFixed(2)}:1`;
};

/** "8s" under a minute, "1:05" above it. */
export const formatDuration = (seconds?: number | null): string | null => {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return null;
  const rounded = Math.round(seconds);
  if (rounded < 60) return `${rounded}s`;
  const minutes = Math.floor(rounded / 60);
  return `${minutes}:${String(rounded % 60).padStart(2, '0')}`;
};

/** File extension from a media URL, uppercased ("PNG", "MP4"). */
export const formatFileType = (url?: string): string | null => {
  if (!url) return null;
  const clean = url.split('?')[0].split('#')[0];
  const ext = clean.slice(clean.lastIndexOf('.') + 1);
  if (!ext || ext.length > 4 || ext.includes('/')) return null;
  return ext.toUpperCase();
};

export interface MediaSpec {
  label: string;
  value: string;
}

const asString = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number') return String(value);
  return null;
};

/**
 * Specs that can't be measured off the file, pulled from the row's `request`
 * JSON. Every key here was observed in the live table; anything missing is
 * simply omitted rather than rendered blank.
 */
export const extractRequestSpecs = (
  request: unknown,
  isVideo: boolean,
): MediaSpec[] => {
  const req = (request && typeof request === 'object' ? request : {}) as Record<
    string,
    unknown
  >;
  const specs: MediaSpec[] = [];

  if (isVideo) {
    // How the clip was seeded. `isTextToVideo`/`isImageToVideo` are the flags
    // the video pipeline writes; the latter holds the source image URL.
    const mode = req.isTalkingVideo
      ? 'Talking video'
      : req.isVideoUpload || req.originalVideo
        ? 'Video to video'
        : req.isFirstLastFrame
          ? 'First & last frame'
          : req.referenceImages || req.references
            ? 'Reference to video'
            : req.isImageToVideo || req.image
              ? 'Image to video'
              : req.isTextToVideo
                ? 'Text to video'
                : null;
    if (mode) specs.push({ label: 'Type', value: mode });

    if (typeof req.generateAudio === 'boolean') {
      specs.push({
        label: 'Audio',
        value: req.generateAudio ? 'Generated' : 'Silent',
      });
    }
  } else {
    const quality = asString(req.quality);
    if (quality) {
      specs.push({
        label: 'Quality',
        value: quality.charAt(0).toUpperCase() + quality.slice(1),
      });
    }

    // An edit carries its source image(s); a plain generation doesn't.
    const sources =
      req.originalImages || req.imageUrls || req.originalImage || req.imageUrl;
    if (sources) {
      const count = Array.isArray(sources) ? sources.length : 1;
      specs.push({
        label: 'Type',
        value: count > 1 ? `Image edit (${count} sources)` : 'Image edit',
      });
    }
  }

  return specs;
};
